import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const details = [
  { icon: Phone, label: "Call us", value: "+91 96228 47390", href: "tel:+919622847390" },
  { icon: Mail, label: "Email", value: "neuronest.rs@gmail.com", href: "mailto:neuronest.rs@gmail.com" },
  { icon: MapPin, label: "Visit", value: "90 Feet Road, Ahmednagar", href: null },
  { icon: Clock, label: "Hours", value: "Mon–Fri · 11:00 am – 6:00 pm", href: null },
];

const empty = { name: "", phone: "", email: "", child_age: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please add your name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        ...form,
        service: form.service || null,
        child_age: form.child_age || null,
        message: form.message || null,
      });
      toast.success("Thank you! We'll reach out to you very soon.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please call us at +91 96228 47390.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-brand-accent/40">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Details */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <p className="text-sm tracking-[0.2em] uppercase text-brand-secondary mb-5">Book a visit</p>
            <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight text-brand-primary leading-tight">
              Let&apos;s start your child&apos;s journey.
            </h2>
            <p className="mt-6 text-lg text-brand-ink/70 max-w-md leading-relaxed">
              Share a few details and our team will get back to you to schedule a warm,
              no-pressure first conversation.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {details.map((d) => {
                const Icon = d.icon;
                const content = (
                  <div className="flex items-start gap-4 rounded-2xl bg-brand-bg border border-brand-primary/10 p-5 h-full">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-primary text-brand-bg">
                      <Icon strokeWidth={1.5} className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-brand-muted">{d.label}</p>
                      <p className="text-brand-primary mt-1">{d.value}</p>
                    </div>
                  </div>
                );
                return d.href ? (
                  <a key={d.label} href={d.href} data-testid={`contact-detail-${d.label.toLowerCase()}`} className="hover:-translate-y-0.5 transition-transform duration-300">
                    {content}
                  </a>
                ) : (
                  <div key={d.label} data-testid={`contact-detail-${d.label.toLowerCase()}`}>{content}</div>
                );
              })}
            </div>

            <div className="mt-6 overflow-hidden rounded-3xl border border-brand-primary/10" data-testid="contact-map">
              <iframe
                title="NeuroNest location — 90 Feet Road, Ahmednagar"
                src="https://www.google.com/maps?q=90+Feet+Road+Ahmednagar&output=embed"
                className="w-full h-64 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={submit}
              data-testid="appointment-form"
              className="rounded-3xl bg-brand-bg border border-brand-primary/10 p-8 sm:p-10 shadow-[0_8px_32px_rgba(46,74,61,0.08)]"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-brand-primary">Parent / Guardian name *</Label>
                  <Input id="name" data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Your name" className="rounded-lg border-brand-muted/40" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-brand-primary">Phone *</Label>
                  <Input id="phone" data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="Mobile number" className="rounded-lg border-brand-muted/40" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-brand-primary">Email *</Label>
                  <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="rounded-lg border-brand-muted/40" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="child_age" className="text-brand-primary">Child&apos;s age</Label>
                  <Input id="child_age" data-testid="input-child-age" value={form.child_age} onChange={set("child_age")} placeholder="e.g. 3 years" className="rounded-lg border-brand-muted/40" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-brand-primary">Service of interest</Label>
                  <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                    <SelectTrigger data-testid="select-service" className="rounded-lg border-brand-muted/40">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ABA Therapy">ABA Therapy</SelectItem>
                      <SelectItem value="Early Intervention Assessment">Early Intervention Assessment</SelectItem>
                      <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="message" className="text-brand-primary">Message</Label>
                  <Textarea id="message" data-testid="input-message" value={form.message} onChange={set("message")} placeholder="Tell us a little about your child..." rows={4} className="rounded-lg border-brand-muted/40" />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="submit-appointment"
                className="mt-8 w-full sm:w-auto rounded-full bg-brand-primary text-brand-bg px-10 py-6 text-sm hover:bg-[#1f332a] transition-colors duration-300"
              >
                {loading ? "Sending..." : "Request appointment"}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
